// src/hooks/useSupabaseUsers.ts

// Safe ENV helper to avoid direct import.meta in Jest/Node
function getEnv(): Record<string, string> {
  const nodeEnv = (typeof process !== 'undefined' ? process.env : {}) as unknown as Record<string, string>;
  return {
    ...nodeEnv,
    ...import.meta.env as unknown as Record<string, string>,
  };
}
const ENV = getEnv();

// Debug: verify env-vars (dev mode only)
if ((ENV.MODE || ENV.NODE_ENV) === 'development') {
  console.log('Supabase URL:', ENV.VITE_SUPABASE_URL || ENV.VITE_SUPABASE_URL);
  console.log('Publishable key present?', Boolean(
    ENV.VITE_SUPABASE_PUBLISHABLE_KEY || ENV.VITE_SUPABASE_ANON_KEY,
  ));
}

import { useState, useEffect, useCallback, useRef } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase, isDemoMode } from '../lib/supabase';
import { User as UserType } from '../types';
import { useEnv } from './useEnv';
import { createShowcaseUsers } from '../demo/showcaseCatalog';
import { mapAuthorityUser } from '../lib/auth/mapAuthorityUser';
import type { UserScopedRelationship } from '../types';

// Module-level canonical accounts for synchronous demo initialization
const DEMO_USERS: UserType[] = createShowcaseUsers();

function mapScopedRelationshipRow(row: Record<string, unknown>): UserScopedRelationship | null {
  const type = typeof row.relationship_type === 'string' ? row.relationship_type : null;
  const resourceType = typeof row.resource_type === 'string' ? row.resource_type : null;
  const resourceId = typeof row.resource_id === 'string' ? row.resource_id : null;
  const accessLevel = typeof row.access_level === 'string' ? row.access_level : null;
  const status = typeof row.status === 'string' ? row.status : null;
  if (!type || !resourceType || !resourceId || !accessLevel || !status) return null;
  return {
    type: type as UserScopedRelationship['type'],
    resourceType: resourceType as UserScopedRelationship['resourceType'],
    resourceId,
    accessLevel: accessLevel as UserScopedRelationship['accessLevel'],
    status: status as UserScopedRelationship['status'],
    effectiveFrom: typeof row.effective_from === 'string' ? row.effective_from : null,
    effectiveUntil: typeof row.effective_until === 'string' ? row.effective_until : null,
    representedCompanyId: typeof row.represented_company_id === 'string' ? row.represented_company_id : null,
  };
}
export function useSupabaseUsers() {
  // In demo mode initialize synchronously — avoids a post-mount re-render
  const [users, setUsers] = useState<UserType[]>(() => isDemoMode ? DEMO_USERS : []);
  const [loading, setLoading] = useState<boolean>(() => !isDemoMode);
  const [error, setError] = useState<string | null>(null);
  const env = useEnv();
  const requestGenerationRef = useRef(0);

  useEffect(() => {
    void fetchUsers();
    return () => {
      requestGenerationRef.current += 1;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isDemoMode) return undefined;
    const pendingTimers = new Set<number>();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'SIGNED_OUT') {
        // Supabase currently warns against making async client calls inside an
        // auth-state callback. Defer the directory refresh beyond that callback
        // and use the session it already supplied instead of re-entering Auth.
        const timer = window.setTimeout(() => {
          pendingTimers.delete(timer);
          void fetchUsers(session);
        }, 0);
        pendingTimers.add(timer);
      }
    });
    return () => {
      pendingTimers.forEach(timer => window.clearTimeout(timer));
      subscription.unsubscribe();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUsers = async (knownSession?: Session | null) => {
    // If Supabase env vars are missing, skip network and use mock data immediately
    if (isDemoMode) {
      // Already initialized synchronously via useState — nothing to do
      return;
    }

    const requestGeneration = ++requestGenerationRef.current;

    if ((ENV.MODE || ENV.NODE_ENV) === 'development') {
      console.log('Fetching users ordered by last_active:', env.supabaseUrl);
    }

    // Guard: supabase can be null in demo mode
    if (!supabase) {
      console.warn('[useSupabaseUsers] Supabase client not available');
      if (requestGeneration === requestGenerationRef.current) setLoading(false);
      return;
    }

    try {
      // The directory is an authenticated surface. During a cold boot the auth
      // provider and this hook mount together, so do not let the anonymous
      // client race ahead into RLS-protected user or relationship reads.
      let session = knownSession;
      if (knownSession === undefined) {
        const sessionResult = await supabase.auth.getSession();
        if (sessionResult.error) throw sessionResult.error;
        session = sessionResult.data.session;
      }
      if (requestGeneration !== requestGenerationRef.current) return;
      if (!session?.user) {
        setUsers([]);
        setError(null);
        return;
      }

      setLoading(true);
      setError(null);
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .order('last_active', { ascending: false }); // Option A: order by existing column

      if (error) throw error;
      if (requestGeneration !== requestGenerationRef.current) return;

      let relationshipRows: unknown[] | null = null;
      let relationshipError: { message?: string } | null = null;
      for (let attempt = 0; attempt < 2; attempt += 1) {
        const relationshipResult = await supabase
          .from('scoped_relationships')
          .select('user_id, relationship_type, resource_type, resource_id, access_level, status, effective_from, effective_until, represented_company_id');
        relationshipRows = relationshipResult.data;
        relationshipError = relationshipResult.error;
        if (requestGeneration !== requestGenerationRef.current) return;
        if (!relationshipError) break;
      }

      if (relationshipError) {
        console.warn('[useSupabaseUsers] scoped_relationships hydrate failed:', relationshipError.message);
      }

      const relationshipsByUser = new Map<string, UserScopedRelationship[]>();
      for (const row of relationshipRows || []) {
        const userId = typeof (row as { user_id?: unknown }).user_id === 'string'
          ? (row as { user_id: string }).user_id
          : null;
        const mapped = mapScopedRelationshipRow(row as Record<string, unknown>);
        if (!userId || !mapped) continue;
        const list = relationshipsByUser.get(userId) ?? [];
        list.push(mapped);
        relationshipsByUser.set(userId, list);
      }

      const formatted: UserType[] = (data || []).map((row) => {
        const mapped = mapAuthorityUser(row as Record<string, unknown>);
        return {
          ...mapped,
          scopedRelationships: relationshipsByUser.get(mapped.id) ?? [],
        };
      });
      if (requestGeneration === requestGenerationRef.current) setUsers(formatted);
    } catch (err) {
      console.error('fetchUsers error:', err);
      if (requestGeneration === requestGenerationRef.current) {
        setUsers([]);
        setError(err instanceof Error ? err.message : 'Unable to load authoritative users');
      }
    } finally {
      if (requestGeneration === requestGenerationRef.current) setLoading(false);
    }
  };

  const updateLastActive = useCallback(async (userId: string) => {
    // Guard: supabase can be null in demo mode
    if (!supabase) {
      return;
    }

    try {
      await supabase
        .from('users')
        .update({ last_active: new Date().toISOString() })
        .eq('id', userId);
    } catch (err) {
      console.error('Failed to update last active:', err);
    }
  }, []);

  // This directory hook is deliberately read-only. User provisioning, invitation,
  // authority changes, suspension, and reactivation live in governed services/RPCs.
  // Supabase Auth admin methods must never run in a browser client.
  return { users, loading, error, updateLastActive, refetch: () => fetchUsers() };
}
